import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { NgModule, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FeatureFlagModule } from '@feature-flag';
import { ItemCacheService } from '@item-cache';
import {
  VudDateAdapter,
  VudDateNativeUTCAdapter,
  VudDrawerModule,
  VudDropdownModule,
} from '@vismaux/ngx-vud';
import {
  GaAlertModule,
  GaDataSelectModule,
  GaDatepickerNativeUtcValueAdapter,
  GaIconModule,
  provideGaDatepickerValueAdapter,
  provideGaFormErrors,
  provideGaTabsI18n,
  provideGaToastOptions,
} from '@vsn-ux/ngx-gaia';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { GaiaAssistantComponent } from './components/gaia-assistant/gaia-assistant.component';
import { HeaderComponent } from './components/header/header.component';
import { LayoutComponent } from './components/layout/layout.component';
import { LoadingComponent } from './components/loading/loading.component';
import { formErrorsFactory } from './core/config/form-errors.config';
import { CoreModule } from './core/core.module';
import { ItemCacheSyncToastService } from './core/services/item-cache-sync-toast.service';
import { EmployeeActionsToastService } from './modules/employee/employee-card/services/employee-actions-toast.service';
import { AuthenticationErrorComponent } from './pages/authentication-error/authentication-error.component';
import { ErrorComponent } from './pages/error/error.component';
import { ServiceUnavailableComponent } from './pages/service-unavailable/service-unavailable.component';
import { SettingsGridComponent } from './pages/settings-grid/settings-grid.component';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { SharedModule } from './shared/shared.module';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    UnauthorizedComponent,
    ServiceUnavailableComponent,
    LayoutComponent,
    ErrorComponent,
    AuthenticationErrorComponent,
    SettingsGridComponent,
  ],
  providers: [
    provideGaDatepickerValueAdapter(GaDatepickerNativeUtcValueAdapter),
    provideGaFormErrors(formErrorsFactory),
    provideGaToastOptions({
      position: 'bottom-left',
    }),
    provideGaTabsI18n(() => ({
      moreLabel: $localize`More`,
    })),
    { provide: VudDateAdapter, useClass: VudDateNativeUTCAdapter },
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } }, // TODO: Remove when popover is default in gaia
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    CoreModule,
    SharedModule,
    FeatureFlagModule,
    VudDrawerModule,
    VudDropdownModule,
    GaIconModule,
    GaAlertModule,
    GaDataSelectModule,
    LoadingComponent,
    GaiaAssistantComponent,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  private employeeActionsToastService = inject(EmployeeActionsToastService);

  private readonly itemCache = inject(ItemCacheService);
  private readonly itemCacheSyncToastService = inject(ItemCacheSyncToastService);

  constructor() {
    ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);
    LicenseManager.setLicenseKey(
      'Using_this_{AG_Grid}_Enterprise_key_{AG-121048}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Visma_Software_AS_(Norway,_Trondheim)}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Visma.net_Payroll_&_HRM}_only_for_{6}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Visma.net_Payroll_&_HRM}_need_to_be_licensed___{Visma.net_Payroll_&_HRM}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{20_February_2027}____[v3]_[01]_MTgwMzA4MTYwMDAwMA==fb715d998a8d773153d0b0e52d66aa5d',
    );
  }
}
